import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { pageFeatureCardBlockName } from '@gik/create/components';
import { Accordion, AccordionItem, GikIconName, SvgIcon } from '@gik/ui';
import Checkbox from '@gik/ui/Checkbox';
import { PropsWithClassName } from '@gik/core/utils/ReactUtils';
import noop from '@gik/core/utils/noop';

export interface IPageFeatureCardProps {
  icon: GikIconName;
  title: string;
  description: string;
  notes?: string;
  inputName?: string;
  asideOpen?: boolean;
  onChange?: (isToggled: boolean) => void;
  value: boolean;
  disabled?: boolean;
  footer?: React.ReactNode;
  recalculateHeightCallback?: (fn: () => void) => void;
}

export default function PageFeatureCard({
  icon,
  title,
  description,
  notes,
  children,
  className,
  inputName,
  asideOpen = true,
  onChange = noop,
  value,
  disabled,
  footer,
  recalculateHeightCallback,
}: PropsWithClassName<React.PropsWithChildren<IPageFeatureCardProps>>) {
  const bem = bemBlock(pageFeatureCardBlockName);

  function handleToggle(toggled: boolean) {
    onChange(toggled);
  }

  return (
    <section className={bem(null, [{ disabled: !value }], className)}>
      <div className={bem('main')}>
        <SvgIcon className={bem('icon')} name={icon} set={'gik-icons'} />
        <section className={bem('copy-container')}>
          <label htmlFor={inputName} className={bem('title')}>
            {title}
          </label>
          <p className={bem('description', [{ 'has-asterisk': Boolean(notes) }])}>{description}</p>
        </section>

        <Checkbox
          className={bem('checkbox')}
          size="xl"
          id={inputName}
          name={inputName}
          checked={value}
          onValueChange={toggled => handleToggle(toggled)}
          disabled={disabled}
        />

        {notes && <p className={bem('notes')}>{notes}</p>}
      </div>

      <div className={bem('secondary-content')}>
        {footer}
        {children && (
          <Accordion allToggled={value && asideOpen} recalculateHeightCallback={recalculateHeightCallback}>
            <AccordionItem>{children}</AccordionItem>
          </Accordion>
        )}
      </div>
    </section>
  );
}
